/**
 * Full Experience Animation Variant (Web)
 *
 * Complete 5-phase animation matching HTML reference:
 * - EMERGENCE (0-55%): Word convergence with chromatic effects
 * - TRANSITION (55-60%): Brief pause
 * - READING (60-82%): Saccadic scroll with focus blur
 * - CONVERGENCE (82-90%): Shrink, center, clear text
 * - REVELATION (90-100%): Text dims to backdrop, badge reveals with delay
 */

import { useState, useMemo, memo } from 'react';
import { View, Text, Pressable, Animated } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTranslation } from '@dramadetector/shared';
import { debugLog, criticalLog } from '../../lib/debug-logger';
import type { LetterThrowingAnimationProps } from './types';
import {
  Y2K_GRADIENT_COLORS,
  NARRATOR_SUBTITLE_CONSTANTS,
} from './types';
import { useSpeedPresets } from './hooks/useSpeedPresets';
import { getCategoryFromScore, getCategoryStyle } from '../../lib/categoryStyles';
import { getRandomQuip } from '../../lib/executiveProducerQuips';
import { NARRATOR_EXPERIENCE_ENABLED } from './narratorFeatureFlags';
import {
  ANIMATION_BG_GRADIENT,
  READING_LINE_HEIGHT,
  READING_PADDING_LINES,
} from './animationHelpers.web';
import { styles } from './animationStyles.web';
import ConvergingLettersView from './ConvergingLettersView.web';
import ReadingPhaseView from './ReadingPhaseView.web';
import DramaBadgeView from './DramaBadgeView.web';
import { useFullExperienceAnimationLoop } from './useFullExperienceLoop.web';

const LINE_HEIGHT = READING_LINE_HEIGHT;
const PADDING_LINES = READING_PADDING_LINES;

const FullExperienceAnimation = memo(function FullExperienceAnimation({
  text,
  speed = '3',
  onComplete,
  onSkip,
  style,
  analysisResult,
}: LetterThrowingAnimationProps) {
  const { t } = useTranslation('home');
  const { settings, wordsForLine } = useSpeedPresets(speed);

  // Category styling
  const category = analysisResult?.category || getCategoryFromScore(analysisResult?.score || 50);
  const categoryStyle = getCategoryStyle(category);

  // Badge animation
  const [badgeScale] = useState(new Animated.Value(0));
  const [badgeOpacity] = useState(new Animated.Value(0));
  const [textOpacity] = useState(new Animated.Value(1));

  // Narrator quip (loading quips only — LLM narrator dead code removed)
  const narratorQuip = useMemo(() => {
    const quip = getRandomQuip();
    return quip.length <= NARRATOR_SUBTITLE_CONSTANTS.MAX_QUIP_LENGTH ? quip : null;
  }, []);

  // Animation loop (extracted to custom hook for <300L compliance)
  const {
    progress,
    phase,
    showSkipHint,
    showContinueButton,
    convergingLetters,
    lineComplete,
    formedText,
    showNarratorSubtitle,
    readingLines,
    currentLineIndex,
    readingScrollY,
    convergenceScale,
    convergenceBlurProgress,
    handleSkip,
    handleContinue,
  } = useFullExperienceAnimationLoop({
    text,
    speed,
    settings,
    wordsForLine,
    analysisResult,
    narratorQuip,
    onComplete,
    onSkip,
    badgeScale,
    badgeOpacity,
    textOpacity,
    totalDurationBase: 15000,
    lineHeight: LINE_HEIGHT,
    paddingLines: PADDING_LINES,
  });

  const outerPointerEvents = showContinueButton ? 'box-none' : 'auto';

  debugLog('event', 'Outer Pressable render', {
    showContinueButton,
    pointerEvents: outerPointerEvents,
    hasOnPress: !showContinueButton,
  });

  return (
    <Pressable
      onPress={() => {
        if (!showContinueButton) {
          handleSkip();
        }
      }}
      pointerEvents={showContinueButton ? 'box-none' : 'auto'}
      style={[styles.container, style]}
      accessibilityLabel={t('home.modern.skip_animation')}
    >
      <LinearGradient
        colors={ANIMATION_BG_GRADIENT}
        style={styles.gradient}
      >
        {/* TOP-LOADER: Y2K Progress Bar */}
        <View style={styles.topLoaderContainer}>
          <LinearGradient
            colors={[Y2K_GRADIENT_COLORS.pink, Y2K_GRADIENT_COLORS.lavender, Y2K_GRADIENT_COLORS.mint]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={[styles.topLoaderBar, { width: `${progress * 100}%` }]}
          />
        </View>

        <View style={styles.content}>
          {/* EMERGENCE: Word Convergence (flying letters) */}
          {(phase === 'emergence' || phase === 'transition') && !lineComplete && (
            <ConvergingLettersView letters={convergingLetters} lineComplete={lineComplete} phase={phase} />
          )}

          {/* FORMED TEXT (after convergence completes, before reading) */}
          {lineComplete && (phase === 'emergence' || phase === 'transition') && (
            <Animated.View style={[styles.formedTextContainer, { opacity: textOpacity }]}>
              <Text style={styles.formedText}>{formedText}</Text>
            </Animated.View>
          )}

          {/* READING + CONVERGENCE + REVELATION phases */}
          <ReadingPhaseView
            phase={phase}
            readingLines={readingLines}
            currentLineIndex={currentLineIndex}
            readingScrollY={readingScrollY}
            convergenceScale={convergenceScale}
            convergenceBlurProgress={convergenceBlurProgress}
            textOpacity={textOpacity}
            paddingLines={PADDING_LINES}
            lineHeight={LINE_HEIGHT}
          />

          {/* Drama badge (revelation phase) */}
          <DramaBadgeView
            analysisResult={analysisResult}
            badgeOpacity={badgeOpacity}
            badgeScale={badgeScale}
            categoryStyle={categoryStyle}
          />

          {/* Skip hint */}
          {showSkipHint && !showContinueButton && (
            <Text style={styles.skipHint}>{t('home.modern.tap_to_skip')}</Text>
          )}

          {/* Narrator subtitle */}
          {NARRATOR_EXPERIENCE_ENABLED && showNarratorSubtitle && narratorQuip && (
            <View pointerEvents="none" style={styles.narratorSubtitleContainer}>
              <View style={styles.narratorSubtitle}>
                <Text style={styles.narratorSubtitleText}>"{narratorQuip}"</Text>
              </View>
            </View>
          )}

          {/* Continue button */}
          {showContinueButton && (
            <Pressable
              onPress={() => {
                criticalLog('button', 'Continue button onPress fired', { showContinueButton });
                handleContinue();
              }}
              style={styles.continueButton as any}
              accessibilityRole="button"
              accessibilityLabel={t('home.modern.view_full_analysis', { defaultValue: 'View Full Analysis' })}
            >
              <LinearGradient
                colors={[Y2K_GRADIENT_COLORS.lavender + '40', Y2K_GRADIENT_COLORS.mint + '30']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={styles.continueButtonGradient}
              >
                <Text style={styles.continueButtonText}>{t('home.modern.view_full_analysis', { defaultValue: 'View Full Analysis' })}</Text>
              </LinearGradient>
            </Pressable>
          )}
        </View>

        {/* Glitch Text Progress Indicator */}
        <View style={styles.glitchProgressContainerInsideGradient} pointerEvents="none">
          <Text style={styles.glitchProgressText}>
            {phase === 'complete' ? t('home.animation_complete') : t('home.animation_analyzing', { percent: String(Math.round(progress * 100)) })}
          </Text>
          <Text style={[styles.glitchProgressText, styles.glitchLayerR]}>
            {phase === 'complete' ? t('home.animation_complete') : t('home.animation_analyzing', { percent: String(Math.round(progress * 100)) })}
          </Text>
          <Text style={[styles.glitchProgressText, styles.glitchLayerB]}>
            {phase === 'complete' ? t('home.animation_complete') : t('home.animation_analyzing', { percent: String(Math.round(progress * 100)) })}
          </Text>
        </View>
      </LinearGradient>
    </Pressable>
  );
});

export default FullExperienceAnimation;
